import 'react-loading-skeleton/dist/skeleton.css'

import { useQueryClient } from '@tanstack/react-query'
import { memo, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import Skeleton from 'react-loading-skeleton'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { AnimatedSpinner } from '../../../../components/AnimatedSpinner'
import { useApproveMentor } from '../../../api/apiHooks'
import { FilterOptions, Profile } from '../../../api/types'
import { CustomSelect } from '../../Selects'
import { CompanySelect } from '../CompanySelect'
import {
  GENDER_DEFAULT_VALUE,
  INDUSTRY_DEFAULT_VALUE,
  RACE_DEFAULT_VALUE,
} from '../constants'
import { DepartmentSelect } from '../DepartmentSelect'
import { JobTitleSelect } from '../JobTitleSelect'

interface ISaveMentorFormProps {
  filtersData?: FilterOptions
  closePopup: () => void
  generatedMentor?: Profile | null
  mentorIsReady: boolean
}

export interface Inputs {
  firstName: string
  lastName: string
  jobTitle: string
  gender: string
  headline: string
  industry: string
  race: string
  department: string
  location: string
  company: string
}

const SaveMentorForm: React.FC<ISaveMentorFormProps> = memo(
  ({ filtersData, closePopup, generatedMentor, mentorIsReady }) => {
    const methods = useForm<Inputs>({ mode: 'onChange' })

    const { register, handleSubmit, setValue, watch } = methods

    const [searchParams] = useSearchParams()
    const [imageSrc, setImageSrc] = useState<string>('')
    const [videoSrc, setVideoSrc] = useState<string>('')

    const { mutateAsync, isPending } = useApproveMentor()
    const queryClient = useQueryClient()
    useEffect(() => {
      if (generatedMentor) {
        setImageSrc(generatedMentor.compressedProfilePictureUrl || '')
        setVideoSrc(generatedMentor.profileIdleLoopLarge || '')
        setValue('firstName', generatedMentor.firstName || '')
        setValue('lastName', generatedMentor.lastName || '')
        setValue('headline', generatedMentor.headline || '')
        setValue('location', generatedMentor.location || '')
        setValue('industry', generatedMentor.industry || '')
        setValue('gender', generatedMentor.gender || '')
        setValue('race', generatedMentor.race || '')
        setValue('company', generatedMentor.company || '')
        setValue('jobTitle', generatedMentor.jobTitle || '')
        setValue('department', generatedMentor.department || '')
      }
    }, [generatedMentor])

    const saveMentor = async () => {
      try {
        await mutateAsync(generatedMentor?.id || '')
        closePopup()
        toast.success('Companion saved successfully')
        if (!searchParams.get('p')) {
          await queryClient.invalidateQueries({ queryKey: ['get-all-mentors'] })
        }
      } catch (error) {
        console.error('Error saving companion:', error)
        toast.error('Error saving companion')
      }
    }
    const deleteMentor = () => {
      closePopup()
    }
    // in future we will be able to edit companion
    const onSelectChange = (name: string, value: string) => {
      setValue(name as keyof Inputs, value)
    }

    return (
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(saveMentor)} className="text-white">
          <div className="px-6 pt-8">
            <div className="flex flex-col items-center justify-center h-full">
              <div className="h-12 w-12 rounded-full bg-[#27AE60] text-white flex items-center justify-center text-2xl ">
                <i className="fa-solid fa-check" />
              </div>
              <div className="text-white text-xl my-2">
                Companion generated successfully
              </div>
            </div>
          </div>
          <div className="py-2 px-6">
            <div className="flex gap-2 h-[162px]">
              <div className="w-1/2 rounded-regular overflow-hidden">
                {mentorIsReady || imageSrc ? (
                  <img src={imageSrc || ''} alt="Companion" />
                ) : (
                  <Skeleton
                    baseColor="#fbfbfb43"
                    highlightColor="#d2d1d13f"
                    className="h-full block"
                    borderRadius="16px"
                  />
                )}
              </div>
              <div className="w-1/2 rounded-regular overflow-hidden ">
                {mentorIsReady ? (
                  videoSrc ? (
                    <video
                      preload="metadata"
                      src={videoSrc || undefined}
                      muted
                      autoPlay
                      loop
                      playsInline
                      controlsList="nodownload"
                      poster={imageSrc || undefined}
                      disablePictureInPicture
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <i className="fa-solid fa-video-slash me-2" />
                      <p>Video is not available</p>
                    </div>
                  )
                ) : (
                  <Skeleton
                    baseColor="#fbfbfb43"
                    highlightColor="#d2d1d13f"
                    className="h-full block"
                    borderRadius="16px"
                  />
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-1/2">
                <label className="text-white text-10px">First Name</label>
                <div className="tc-mv-input-wrapper">
                  <input
                    {...register('firstName')}
                    className="w-full border border-gray-300 rounded p-2 text-black"
                    placeholder="John"
                    disabled
                  />
                </div>
              </div>
              <div className="w-1/2">
                <label className="text-white text-10px">Last Name</label>
                <div className="tc-mv-input-wrapper">
                  <input
                    {...register('lastName')}
                    className="w-full border border-gray-300 rounded p-2 text-black"
                    placeholder="Doe"
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="w-1/2">
                <label className="text-white text-10px">Gender</label>
                <CustomSelect
                  onSelectChange={onSelectChange}
                  options={[
                    GENDER_DEFAULT_VALUE,
                    ...(filtersData?.gender || []),
                  ]}
                  selectedOption={watch().gender || GENDER_DEFAULT_VALUE}
                  name="gender"
                  disabled
                />
              </div>
              <div className="w-1/2">
                <label className="text-white text-10px">Race</label>
                <CustomSelect
                  onSelectChange={onSelectChange}
                  options={[RACE_DEFAULT_VALUE, ...(filtersData?.race || [])]}
                  selectedOption={watch().race || RACE_DEFAULT_VALUE}
                  name="race"
                  disabled
                />
              </div>
            </div>
            <div>
              <div className="flex gap-2">
                <div className="w-1/2">
                  <label className="text-white text-10px">Industry</label>
                  <CustomSelect
                    onSelectChange={onSelectChange}
                    options={[
                      INDUSTRY_DEFAULT_VALUE,
                      ...(filtersData?.industry || []),
                    ]}
                    selectedOption={watch().industry || INDUSTRY_DEFAULT_VALUE}
                    name="industry"
                    disabled
                  />
                </div>
                <div className="w-1/2">
                  <label className="text-white text-10px">Company</label>
                  <CompanySelect
                    defaultValue={watch().company || ''}
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className="mt-2">
              <div className="flex gap-2">
                <div className="w-1/2">
                  <label className="text-white text-10px">Department</label>
                  <DepartmentSelect
                    defaultValue={watch().department || ''}
                    disabled
                  />
                </div>
                <div className="w-1/2">
                  <label className="text-white text-10px">Job Title</label>
                  <JobTitleSelect
                    defaultValue={watch().jobTitle || ''}
                    disabled
                  />
                </div>
              </div>
            </div>
            <div className="mt-2">
              <label className="text-white text-10px">Location</label>
              <div className="tc-mv-input-wrapper">
                <input
                  {...register('location')}
                  className="w-full border border-gray-300 rounded p-2 text-black"
                  placeholder="New York, USA"
                  disabled
                />
              </div>
            </div>
            <div className="mt-2">
              <label className="text-white text-10px">Headline</label>
              <div className="tc-mv-input-wrapper">
                <input
                  {...register('headline')}
                  className="w-full border border-gray-300 rounded p-2 text-black"
                  placeholder="Empowering professionals to achieve career success through strategic coaching"
                  disabled
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-white/10 py-4 px-6">
            <button className="btn btn-secondary" onClick={deleteMentor}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!mentorIsReady || isPending}
            >
              {isPending && <AnimatedSpinner className="me-2" />}
              Save companion
            </button>
          </div>
        </form>
      </FormProvider>
    )
  },
)

export { SaveMentorForm }
